import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, EyeOff, Sparkles, Wand2 } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { useShopPath } from '@/hooks/useShopPath';
import { formatPct, pctForBar } from '@/lib/format-pct';

/**
 * Surfaces the highest-priority product profile gap on the Inventory page,
 * rotating through attributes so the card never goes silent while there's
 * still work to do. Priority follows the same order as ProfileCompletenessBar
 * (title → image → description → brand → category).
 *
 * Two visual treatments:
 *   - title gap → destructive "Priority fix / Invisible to shoppers" — a
 *     title-less product can't render anywhere, so the strongest signal.
 *   - any other gap → soft "✨ Fix" tone with the attribute named in both
 *     the subtitle and the CTA (Max's design, NEA-5840). These are quality
 *     gaps that widen channel reach, not visibility blockers.
 *
 * Renders nothing when every attribute is at 100%.
 */
const ATTRIBUTE_PRIORITY = ['title', 'image', 'description', 'brand', 'category'];

const QUALITY_META = {
	image: { issue: 'missing-image', noun: 'images', rowLabel: 'Missing images', cta: 'Add images' },
	description: { issue: 'missing-description', noun: 'descriptions', rowLabel: 'Missing descriptions', cta: 'Add descriptions' },
	brand: { issue: 'missing-brand', noun: 'brand', rowLabel: 'Missing brand', cta: 'Add brand' },
	category: { issue: 'missing-category', noun: 'storefront categories', rowLabel: 'Missing storefront category', cta: 'Add categories' }
};

const QUALITY_BODY = 'Better product profiles attract more shoppers and lift revenue.';

const TONE = {
	destructive: {
		card: 'border-destructive/20 bg-destructive/5',
		eyebrow: 'text-destructive',
		headline: 'text-destructive',
		pct: 'text-destructive',
		bar: 'bg-destructive'
	},
	warning: {
		card: 'border-warning/30 bg-warning-soft/40',
		eyebrow: 'text-warning-soft-foreground',
		headline: 'text-warning-soft-foreground',
		pct: 'text-warning-soft-foreground',
		bar: 'bg-warning'
	}
};

export function InvisibleProductsCard({ profileCompleteness = [], inStockItems = 0 }) {
	const { shopPath } = useShopPath();
	const byAttr = Object.fromEntries(profileCompleteness.map((s) => [s.attribute, s]));
	const attribute = ATTRIBUTE_PRIORITY.find((attr) => (byAttr[attr]?.missingCount ?? 0) > 0);
	if (!attribute) return null;

	const missingCount = byAttr[attribute].missingCount;
	const missingPctLabel = formatPct(missingCount, inStockItems);
	const missingBarPct = pctForBar(missingCount, inStockItems);

	if (attribute === 'title') {
		const tone = TONE.destructive;
		return (
			<SoftCard className={`flex flex-col ${tone.card} !p-5 md:!p-6`}>
				<div className="min-w-0">
					<div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${tone.eyebrow}`}>
						<AlertTriangle className="h-3.5 w-3.5" /> Priority fix
					</div>
					<div className="mt-2 flex flex-wrap items-baseline gap-2">
						<span className={`text-3xl font-extrabold tracking-tight tabular-nums ${tone.headline} md:text-4xl`}>
							{missingCount.toLocaleString()}
						</span>
						<span className="text-sm tabular-nums text-muted-foreground">
							of {inStockItems.toLocaleString()} in-stock products
						</span>
					</div>
				</div>

				<div className="mt-5 space-y-3">
					<div>
						<div className="mb-1.5 flex items-baseline justify-between gap-3">
							<div className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
								<EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
								Invisible to shoppers
							</div>
							<div className={`text-sm font-bold tabular-nums ${tone.pct}`}>{missingPctLabel}</div>
						</div>
						<div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
							<div className={`absolute inset-y-0 left-0 rounded-full ${tone.bar}`} style={{ width: `${missingBarPct}%` }} />
						</div>
					</div>
				</div>

				<div className="mt-5 flex flex-col items-start gap-3 border-t border-border/60 pt-4">
					<p className="text-xs text-muted-foreground">Add product titles so these products go live.</p>
					<Link
						to={shopPath('/inventory/manage?issue=missing-title')}
						className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
					>
						Fix now <ArrowRight className="h-3.5 w-3.5" />
					</Link>
				</div>
			</SoftCard>
		);
	}

	const meta = QUALITY_META[attribute];
	const tone = TONE.warning;
	return (
		<SoftCard className={`flex flex-col ${tone.card} !p-5 md:!p-6`}>
			<div className="min-w-0">
				<div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${tone.eyebrow}`}>
					<Sparkles className="h-3.5 w-3.5" /> Fix
				</div>
				<div className="mt-2 flex flex-wrap items-baseline gap-2">
					<span className={`text-3xl font-extrabold tracking-tight tabular-nums ${tone.headline} md:text-4xl`}>
						{missingCount.toLocaleString()}
					</span>
					<span className="text-sm tabular-nums text-muted-foreground">
						of {inStockItems.toLocaleString()} in-stock products · {missingPctLabel} missing {meta.noun}
					</span>
				</div>
			</div>

			<div className="mt-5 space-y-3">
				<div>
					<div className="mb-1.5 flex items-baseline justify-between gap-3">
						<div className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
							<Wand2 className="h-3.5 w-3.5 text-muted-foreground" />
							{meta.rowLabel}
						</div>
						<div className={`text-sm font-bold tabular-nums ${tone.pct}`}>{missingPctLabel}</div>
					</div>
					<div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
						<div className={`absolute inset-y-0 left-0 rounded-full ${tone.bar}`} style={{ width: `${missingBarPct}%` }} />
					</div>
				</div>
			</div>

			<div className="mt-5 flex flex-col items-start gap-3 border-t border-border/60 pt-4">
				<p className="text-xs text-muted-foreground">{QUALITY_BODY}</p>
				<Link
					to={shopPath(`/inventory/manage?issue=${meta.issue}`)}
					className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
				>
					{meta.cta} <ArrowRight className="h-3.5 w-3.5" />
				</Link>
			</div>
		</SoftCard>
	);
}
